// L7 (boot-health #3986): make config-DB initialization fail safe instead of hanging.
//
// ConfigDbService.initializeAsync() ended with `this.loaded.resolve(true)` and had no
// try/catch. If anything before that line threw (the commonDb open, the config-record
// read), `loaded` — a DeferredPromise every getConfigValueAsync() awaits — stayed pending
// forever. Result: every feature-flag-gated code path that awaits a config value froze,
// and the throw leaked as an unhandled rejection (config init runs fire-and-forget on boot).
//
// This wraps the init in try/catch: on failure we report the problem and resolve `loaded`
// with false, so getConfigValueAsync() falls through and returns undefined (callers use
// their own defaults) instead of hanging. The decision is a pure, node-loadable helper
// (ConfigDbService itself pulls in Angular/PouchDB and can't load under node) so it can be
// unit-tested; the service supplies the real work + side effects.
//
// #6089: until global_common's first sync of this session settles, the config read goes
// to the SERVER (the pass-through window, design-docs/pass-through-db.md). A short network
// drop at that moment used to leave every config value at its default for the whole
// session, because nothing read the config again. A transient failure now serves the copy
// already on the device, then reads again once the sync has settled. See
// design-docs/pass-through-db.md, "Config read at boot recovers from a transient failure".

/** How the retry read after a transient failure ended, when it did not throw. */
export enum ConfigReloadOutcome {
  /** the config was read again and the cache now matches it */
  Reloaded = 'Reloaded',
  /** the user signed out while the retry waited, so the config was deliberately not read */
  DatabaseClosing = 'DatabaseClosing',
}

/** What a recovered transient failure tells the reporter. */
export interface ConfigDbRecoveryReport {
  /** did the local copy hold config documents, so `loaded` resolved true straight away? */
  servedFromLocalCopy: boolean;
  /** the transient error the first read failed with */
  originalError: unknown;
  /** set when the local-copy read itself threw; the retry still ran */
  localCopyError?: unknown;
}

export interface ConfigDbInitDeps {
  /** Do the real initialization (open commonDb, read + populate config records). */
  runInit: () => Promise<void>;
  /** Resolve the `loaded` deferred: true on success, false on failure (so awaiters proceed). */
  resolveLoaded: (ok: boolean) => void;
  /** Surface an init failure (log + trackSilentProblem). Must not throw. */
  reportFailure: (err: unknown) => void;
  /** Is this error a passing network failure (the real one is `isTransientFetchError`)? */
  isTransient: (err: unknown) => boolean;
  /** Fill the cache from the copy already on the device, never the network. Answers
   *  whether that copy held any config documents. */
  loadFromLocalCopyAsync: () => Promise<boolean>;
  /** Wait until global_common's initial sync settles (or the wait gives up). Never throws. */
  waitForSyncSettledAsync: () => Promise<void>;
  /** Read the config again through the normal path, notifying watchers of changed values.
   *  Answers `DatabaseClosing`, without reading, when the user signed out meanwhile. */
  reloadAsync: () => Promise<ConfigReloadOutcome>;
  /** Log that the retry was skipped because the user signed out. Must not throw. */
  noteDatabaseClosing: () => void;
  /** Surface that a transient failure was repaired (trackSilentInfo). Must not throw. */
  reportRecovered: (report: ConfigDbRecoveryReport) => void;
}

export async function runConfigDbInitAsync(deps: ConfigDbInitDeps): Promise<void> {
  try {
    await deps.runInit();
    deps.resolveLoaded(true);
    return;
  } catch (err) {
    // if the failure is not a passing network drop, it is unexpected: report, then
    // resolve false — NEVER leave `loaded` pending, or every getConfigValueAsync()
    // await hangs forever.
    if (!deps.isTransient(err)) {
      deps.reportFailure(err);
      deps.resolveLoaded(false);
      return;
    }
    await recoverFromTransientFailureAsync(deps, err);
  }
}

// a transient failure: serve the local copy now, read again once the sync has settled
async function recoverFromTransientFailureAsync(deps: ConfigDbInitDeps, originalError: unknown): Promise<void> {
  // resolve `loaded` from the local copy straight away. one-shot readers (the Android
  // SQLite rollback decision, getConfigValueAsync callers) get real values instead of
  // defaults, and nobody waits for the sync. with no local copy (a fresh install) they
  // get defaults, as before #6089.
  let servedFromLocalCopy = false;
  let localCopyError: unknown;
  try {
    servedFromLocalCopy = await deps.loadFromLocalCopyAsync();
  } catch (err) {
    // the retry below still runs; the error travels with whichever report it sends
    localCopyError = err;
  }
  deps.resolveLoaded(servedFromLocalCopy);

  // once the sync has settled the read goes to the local database, so this second
  // read no longer depends on the network
  let outcome: ConfigReloadOutcome;
  try {
    await deps.waitForSyncSettledAsync();
    outcome = await deps.reloadAsync();
  } catch (retryErr) {
    // still unresolved: this is what ConfigDbInitFailed is for
    deps.reportFailure(retryErr);
    return;
  }
  // if the user signed out meanwhile, nothing was repaired and nothing is wrong
  if (outcome === ConfigReloadOutcome.DatabaseClosing) {
    deps.noteDatabaseClosing();
    return;
  }
  deps.reportRecovered({
    servedFromLocalCopy,
    originalError,
    ...(localCopyError !== undefined ? { localCopyError } : {}),
  });
}

/** One key a full read of the config documents returned. */
export interface ConfigCacheEntry<R> {
  key: string;
  record: R;
  value: unknown;
}

/** One subscriber notification: a key whose resolved value changed. */
export interface ConfigCacheNotification {
  key: string;
  newValue: unknown;
  oldValue: unknown;
}

/** What `planConfigCacheUpdate` decided; the service applies it. */
export interface ConfigCacheUpdate<R> {
  /** every key the read returned, to be stored */
  set: ConfigCacheEntry<R>[];
  /** keys to remove from the cache */
  drop: string[];
  /** one entry per key whose value changed, in the order to notify */
  notify: ConfigCacheNotification[];
}

/**
 * Decide how a full read of the `config/` documents changes the cache: store every key
 * the read returned, and notify subscribers for each key whose resolved value changed.
 *
 * `droppableKeys` are the keys a previous read filled from the LOCAL copy (#6089). One of
 * them that this read no longer returns was deleted on the server, so it is dropped and
 * its subscribers told. No other key is ever dropped: a key `handleConfigChanges` added
 * from a new document while this read was in flight is simply missing from a read that
 * started before it existed, and dropping it would lose it for the session.
 */
export function planConfigCacheUpdate<R extends { _id: string }>(
  currentValues: Readonly<Record<string, unknown>>,
  records: readonly R[],
  resolve: (record: R) => unknown,
  droppableKeys: ReadonlySet<string>,
): ConfigCacheUpdate<R> {
  const update: ConfigCacheUpdate<R> = { set: [], drop: [], notify: [] };
  const seenKeys = new Set<string>();
  for (const record of records) {
    // keys all start with "config/", so we assure that prefix is there and remove it
    const key = record._id.match(/^config\/(.*)/)?.[1];
    if (!key) continue;
    seenKeys.add(key);

    const value = resolve(record);
    update.set.push({ key, record, value });
    if (value !== currentValues[key]) {
      update.notify.push({ key, newValue: value, oldValue: currentValues[key] });
    }
  }

  // for every local-copy key the read no longer returns, fall back to the default
  for (const key of droppableKeys) {
    if (seenKeys.has(key)) continue;
    update.drop.push(key);
    if (currentValues[key] !== undefined) {
      update.notify.push({ key, newValue: undefined, oldValue: currentValues[key] });
    }
  }
  return update;
}
